import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { FaDownload, FaPlus, FaEye, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import MaterialReceiptNoteFormModal from '../components/MaterialReceiptNoteFormModal';
import MaterialReceiptViewModal from '../components/MaterialReceiptViewModal';
import PDFDesigner from '../components/pdfDesigner';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';

import { useNavigate } from 'react-router-dom';

// Add this component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = '/api';

const COLORS = {
  ANGEL: '#EAF9FB',
  THALASSA: '#D5F0F5',
  DIVIDER: '#E6EEF2',
  ACCENT: '#0f766e',
  ACCENT_HOVER: '#2b8f85',
  CARD_BORDER: '#e6eef0'
};

// Add this component inside your file
// Add this component inside your file (replacing the old one)
// Add this component inside your file (replacing the old one)
// Add this component inside your file (replacing the old one)
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;

  // Dynamic styling based on whether it's a dangerous action (Delete) or not
  const iconBgColor = isDanger ? 'bg-red-100' : 'bg-teal-100';
  const iconTextColor = isDanger ? 'text-red-600' : 'text-teal-600';
  
  const confirmButtonClass = isDanger 
    ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500' 
    : 'bg-teal-700 hover:bg-teal-800 text-white focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900 bg-opacity-50  transition-opacity"
        onClick={onCancel}
      ></div>
      
      {/* Modal Content */}
      <div className="relative bg-white  shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200">
        
        {/* ICON SECTION */}
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
          {isDanger ? (
            // Warning Icon (Exclamation) for Delete
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          ) : (
            // Info/Check Icon for Approve/Validate/Send
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          )}
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        
        {/* Message */}
        <p className="text-sm text-gray-500 mb-6">
          {message}
        </p>
        
        {/* Buttons */}
        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmButtonClass}`}
          >
            {title.includes('Delete') ? 'Delete' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add this new component after ConfirmationDialog
const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;

  let icon, title, bgColor, textColor;

  if (status === 'loading') {
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-600';
    title = 'Processing...';
    icon = <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>;
  } else if (status === 'success') {
    bgColor = 'bg-green-100';
    textColor = 'text-green-600';
    title = 'Done';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    );
  } else {
    // Error
    bgColor = 'bg-red-100';
    textColor = 'text-gray-500';
    title = 'Error';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 ">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>
          {icon}
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        
        
<p className={`text-sm ${textColor} mb-6`}>
  {message}
</p>
        
        {status === 'error' && (
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const MaterialReceiptNote = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  
  // --- ADDED: Multi-select filter state for BC Status ---
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  // -----------------------------------------------------

  const [mrnData, setMrnData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
   const [summaryData, setSummaryData] = useState({
    pendingApproval: 0,
    pendingValidate: 0,
    pendingSendToBC: 0,
    thisMonth: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    isDanger: false
  });

    // State for the Approval/Reject Modal
     const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    mrnDocumentId: null,
    mrnNumber: null,   // Kept for display in the modal
    currentLevel: null,
    remarks: ''
  });

    const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  const navigate = useNavigate();
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'MRNNumber', direction: 'desc' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
   const [totalPages, setTotalPages] = useState(0); 
  const [itemsPerPage] = useState(6);
  
  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

    // Add this state inside MaterialReceiptNote component
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    status: 'loading', // 'loading' | 'success' | 'error'
    message: ''
  });

  // Add this new state
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    id: null,
    mrnNo: '',
    action: ''
  });
  
  // Fetch MRN data from API
  useEffect(() => {
    fetchMRNData();
  }, []);
  
   const fetchMRNData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const params = new URLSearchParams({
        page: page,
        limit: itemsPerPage,
        search: searchTerm,
        status: status,
        sortBy: sortConfig.key || 'MRNDate',
        sortOrder: sortConfig.direction || 'desc'
      });

      // --- ADD THIS: Send the selected filters (e.g. "Pending,Validate") to the backend ---
      if (selectedStatusFilters.length > 0) {
        params.append('processStatus', selectedStatusFilters.join(','));
      }

      const response = await axios.get(`${API_URL}/mrn-filter?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;

      // Set data from server (Server now handles ALL filtering)
      setMrnData(data.mrns);
      
      // Update stats from server response
      setSummaryData({
        pendingApproval: data.stats.pendingApproval || 0,
        pendingValidate: data.stats.pendingValidate || 0,
        pendingSendToBC: data.stats.pendingSendToBC || 0,
        thisMonth: data.stats.createdThisMonth || 0
      });

      // Update pagination info
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);

    } catch (error) {
      console.error('Error fetching MRN data:', error);
      setMrnData([]);
      setSummaryData({ pendingApproval: 0, pendingValidate: 0, pendingSendToBC: 0, thisMonth: 0 });
    } finally {
      setLoading(false);
    }
  };

    // Debounced Effect for Search, Status, and Sort changes
   // Debounced Effect for Search, Status, Sort, and PROCESS STATUS Filters
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      // Reset to page 1 when search, sort, or process status changes
      setCurrentPage(1); 
      fetchMRNData(1);
    }, 500); 

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, status, sortConfig, selectedStatusFilters]); // <--- Added selectedStatusFilters here

  // Effect for Page Changes
  useEffect(() => {
    if (currentPage > 0) {
        fetchMRNData(currentPage);
    }
  }, [currentPage]);
  
  const downloadPDF = async (mrnNumber) => {
    try {
      const response = await axios.get(`${API_URL}/designs/Material Receipt Note/pdf/${mrnNumber}`, {
        responseType: 'arraybuffer'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `MRN_${mrnNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error('Error downloading PDF:', error);
      // alert(`Failed to download PDF: ${error.message}`);
      showToast(`Failed to download PDF: ${error.message}`, 'error', 'Error');
    }
  };

  // Handle CSV export with actual data
  const handleDownloadCSV = () => {
    const headers = [
      'MRN Number',
      'MRN Date',
      'PO Number',
      'Vendor Name',
      'Received By',
      'Total Items',
      'Bill Date',
      'Project',
       'Vendor Invoice No.',
      'Status',
      'Remarks',
    ];
    
    // Use actual data from API
    const rows = mrnData.map(item => ({
      mrnNumber: item.MRNNumber || '',
      mrnDate: item.MRNDate ? new Date(item.MRNDate).toLocaleDateString() : '',
      poNumber: item.PurchaseOrderNumber || '',
      vendorName: item.vendor_name || '',
      receivedBy: item.ReceivedBy || '',
      totalItems: item.TotalItems || 0,
      billDate: item.BillDate ? new Date(item.BillDate).toLocaleDateString() : '',
      projectCode: item.ProjectCode || '',
  vendorInvoiceNo: item.VendorInvoiceNo || '',
      status: item.Status === 1 || item.Status === true ? 'Active' : 'Inactive',
      remarks: item.Remarks || '',
    }));
    
    const csvContent =
      [headers, ...rows.map(row => [
        row.mrnNumber,
        row.mrnDate,
        row.poNumber,
        row.vendorName,
        row.receivedBy,
        row.totalItems,
        row.billDate,
         row.projectCode,
    row.vendorInvoiceNo,
        row.status,
        row.remarks,
      ])]
        .map(e => e.join(','))
        .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'MaterialReceiptNotes.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleCreate = () => {
    setEditData(null);
    setIsModalOpen(true);
  };
  
  const handleEdit = (data) => {
    setEditData(data.MRNDocumentId);
    setIsModalOpen(true);
  };
  
  const handleView = (data) => {
     setViewData(data.MRNDocumentId);
    setIsViewModalOpen(true);
  };
  

    // Opens the modal when user clicks the button
  const openApprovalModal = (mrnDocumentId,mrnNumber, level) => {
    setApprovalModal({
      isOpen: true,
      mrnNumber: mrnNumber,
      mrnDocumentId: mrnDocumentId,
      currentLevel: level,
      remarks: '' // Reset remarks
    });
  };

  // Handles the actual API call (Approve or Reject)
    const handleApprovalAction = async (action) => {
     const { mrnDocumentId, remarks } = approvalModal;

    if (action === 'REJECTED' && !remarks.trim()) {
      // Using a simple alert for validation as requested to keep changes minimal, 
      // or you could use the ActionModal for this too.
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when rejecting.', 'error', 'Validation Error');
      return;
    }

    if (action === 'APPROVED' && !remarks.trim()) {
      // Using a simple alert for validation as requested to keep changes minimal, 
      // or you could use the ActionModal for this too.
      // alert('Remarks are mandatory when approving.');
      showToast('Remarks are mandatory when approving.', 'error', 'Validation Error');
      return;
    }


    // Close the remarks modal first
    setApprovalModal({ ...approvalModal, isOpen: false });

    // Start Loading
    setActionModal({ isOpen: true, status: 'loading', message: `${action === 'APPROVED' ? 'Approving' : 'Rejecting'} MRN...` });

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = action === 'APPROVED' 
        ? `${API_URL}/mrn/approve` 
        : `${API_URL}/mrn/reject`;

      await axios.post(endpoint, 
        { mrnDocumentId, remarks }, 
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      fetchMRNData();
      // Show Success
      setActionModal({ isOpen: true, status: 'success', message: `MRN ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully` });
      setTimeout(() => setActionModal({ isOpen: false }), 2000);
      // showToast(`MRN ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully`, 'success', 'Done');
    } catch (error) {
      // console.error(`Error:`, error);
      // Show Error
      setActionModal({ 
        isOpen: true, 
        status: 'error', 
        message: `Error: ${error.response?.data?.message || error.message}` 
      });
      // showToast(`Error: ${error.response?.data?.message || error.message}`, 'error', 'Error');
    }
  };
  
  // Updated handleDelete function with better error handling
    const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete MRN',
      message: `Are you sure you want to delete MRN ${id}?`,
      isDanger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await axios.delete(`${API_URL}/mrn/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          
          if (response.status === 200) {
            fetchMRNData();
            showToast('Material Receipt Note deleted successfully', 'success', 'Done');
          }
        } catch (error) {
          // console.error('Error deleting MRN:', error);
          
          let errorMessage = 'Error deleting record';
          if (error.response) {
             if (error.response.data && error.response.data.message) {
               errorMessage = error.response.data.message;
               if (errorMessage.includes('constraint') || errorMessage.includes('reference')) {
                 errorMessage = 'Cannot delete. Record is referenced by other documents.';
               }
             }
          } else if (error.message) {
            errorMessage = error.message;
          }
          showToast(errorMessage, 'error', 'Error');
        }
      }
    });
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };
  
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewData(null);
  };
  
  const handleFormSubmit = () => {
    // Refresh data after form submission
    fetchMRNData();
  };
  
  // Toggle MRN status
  const toggleMRNStatus = async (mrnNumber, currentStatus) => {
    try {
      const token = localStorage.getItem('authToken');
      const newStatus = currentStatus ? 0 : 1;
      
      await axios.put(`${API_URL}/mrn/${mrnNumber}`, {
        Status: newStatus
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the list
      fetchMRNData();
      // alert(`Material receipt note ${newStatus ? 'activated' : 'deactivated'} successfully`);
      showToast(`Material receipt note ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success', 'Done');
    } catch (error) {
      // console.error('Error updating material receipt note status:', error);
      // alert('Error updating material receipt note status');
      showToast('Error updating material receipt note status', 'error', 'Error');
    }
  };

  // Generic helper to update a specific field on the MRN
  const updateMRNField = async (mrnNumber, field, value, successMsg) => {
    try {
      const token = localStorage.getItem('authToken');
      
      await axios.put(`${API_URL}/mrn/${mrnNumber}`, {
        [field]: value
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      fetchMRNData();
      // alert(successMsg);
      showToast(successMsg, 'success', 'Done');
    } catch (error) {
      // console.error(`Error updating ${field}:`, error);
      // alert(`Error updating status: ${error.message}`);
      showToast(`Error updating status: ${error.message}`, 'error', 'Error');
    }
  };


  // Updated Handle Approve By Admin
    // Updated Approve Handler
  const handleApproveByAdmin = (mrnNumber) => {
    setConfirmModal({
      isOpen: true,
      title: 'Approve MRN',
      message: `Are you sure you want to approve MRN ${mrnNumber}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          await axios.post(`${API_URL}/mrn/approve`, 
            { mrnNumber }, 
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          fetchMRNData();
          // alert('MRN Approved successfully'); 
          showToast('MRN Approved successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error approving MRN:', error);
          // alert(`Error: ${error.response?.data?.message || error.message}`);
          showToast(`Error: ${error.response?.data?.message || error.message}`, 'error', 'Error');
        }
      },
      isDanger: false
    });
  };

  // Updated Validate Handler
    const handleValidateData = (mrnDocumentId,mrnNumber) => {
    setConfirmModal({
      isOpen: true,
      title: 'Validate Data',
      message: `Are you sure you want to validate the data for MRN ${mrnNumber}?`,
      onConfirm: async () => {
        // Start Loading
        setActionModal({ isOpen: true, status: 'loading', message: 'Validating Data...' });
        try {
          const token = localStorage.getItem('authToken');
          await axios.post(`${API_URL}/mapping/mrn/validate`, 
            { mrnDocumentId  },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          fetchMRNData();
          // Show Success
          setActionModal({ isOpen: true, status: 'success', message: 'Data Validated successfully' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Data validated successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error validating MRN data:', error);
          // Show Error
          setActionModal({ 
            isOpen: true, 
            status: 'error', 
            message: `Error: ${error.response?.data?.message || error.message}` 
          });
          // showToast(`Error: ${error.response?.data?.message || error.message}`, 'error', 'Error');
        }
      },
      isDanger: false
    });
  };
  
  // Updated Send to BC Handler
    const handleSendToBC = (mrnDocumentId ,mrnNumber) => {
    setConfirmModal({
      isOpen: true,
      title: 'Send to BC',
      message: `Are you sure you want to send MRN ${mrnNumber} to BC?`,
      onConfirm: async () => {
        // Start Loading
        setActionModal({ isOpen: true, status: 'loading', message: 'Sending to business central...' });
        try {
          const token = localStorage.getItem('authToken');
          await axios.post(`${API_URL}/mapping/mrn/send-to-bc`, 
            { mrnDocumentId  },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          fetchMRNData();
          // Show Success
          setActionModal({ isOpen: true, status: 'success', message: 'Data sent to BC successfully' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Data sent to BC successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error sending MRN:', error);
          // // Show Error
          setActionModal({ 
            isOpen: true, 
            status: 'error', 
            message: `Error: ${error.response?.data?.message || error.message}` 
          });
          // showToast(`Error: ${error.response?.data?.message || error.message}`, 'error', 'Error');
        }
      },
      isDanger: false
    });
  };

    const showToast = (message, type = 'success', title = 'Done', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };
  
  // Handle sorting
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };
  
  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      // Handle different data types
      switch(sortConfig.key) {
        case 'MRNNumber':
        case 'vendor_name':
        case 'ReceivedBy':
        case 'PendingBillNumber':
        case 'Remarks':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'MRNDate':
        case 'BillDate':
          aValue = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
          bValue = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
          break;
        case 'TotalItems':
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
          break;
        case 'Status':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }
      
      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };
  
  // Helper function to render sort arrows
  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';
    
    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${isAsc ? 'text-blue-600' : 'text-gray-500'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${isDesc ? 'text-blue-600' : 'text-gray-500'}`} />
      </span>
    );
  };
  
  // Filter MRN data based on search term and status
   // Filter MRN data based on search term and status
  const filteredMrnData = mrnData.filter(item => {
    const matchesSearch = searchTerm === '' || 
      (item.MRNNumber && item.MRNNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.vendor_name && item.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.PurchaseOrderNumber && item.PurchaseOrderNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.ReceivedBy && item.ReceivedBy.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.PendingBillNumber && item.PendingBillNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const isActive = item.Status === 1 || item.Status === true;
    const matchesStatus = status === 'All' || 
      (status === 'Active' && isActive) || 
      (status === 'Inactive' && !isActive);
    
    // --- UPDATED: BC Status Filter Logic to match JSON structure ---
    const matchesBcStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.some(filter => {
      
      // 1. Approval Phase (Checking ApprovalStatus string)
      if (filter === 'Pending') return item.ApprovalStatus === 'PENDING';
      if (filter === 'Approved') return item.ApprovalStatus === 'APPROVED';
      if (filter === 'Rejected') return item.ApprovalStatus === 'REJECTED';

      // 2. Post-Approval Phase (Checking Boolean flags)
      // "Validated" means Approved & Validated but NOT yet sent to BC
      if (filter === 'Validate') return item.ValidateData !== true && item.SendToBC !== true;

      // "Sent to BC" means the process is complete

       if (filter === 'Send to BC')
      return (
        !item.SendToBC && item.ValidateData === true &&
        (item.ApprovalStatus === 'APPROVED' || item.ApprovalStatus == null)
      );
     
        if (filter === 'Data Successfully Sent') return item.ValidateData && item.SendToBC;
      return false;
    });
    // -----------------------------------------------------------

    return matchesSearch && matchesStatus && matchesBcStatus;
  });
  // --- ADDED: Handler for BC Status Filter ---
  const handleStatusFilterChange = (filterValue) => {
    setSelectedStatusFilters(prev => 
      prev.includes(filterValue) 
        ? prev.filter(item => item !== filterValue) 
        : [...prev, filterValue]
    );
  };
  // ------------------------------------------

   const handleLogout = () => {
    navigate('/login');
  };
  
  // Handler to trigger success message
  const handleFormSuccess = (result, actionType) => {
    setTimeout(() => {
      setSuccessModal({
        isOpen: true,
        id: result.MRNNumber,
        mrnNo: result.MRNNumber,
        action: actionType // 'Created' or 'Updated'
      });
    }, 300); // Small delay to allow form to start closing
  };

  // Handler to close the success message
  const handleCloseSuccess = () => {
    setSuccessModal({ isOpen: false, id: null, mrnNo: '', action: '' });
    fetchMRNData(); // Refresh list
  };

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (successModal.isOpen) {
      const timer = setTimeout(() => {
        handleCloseSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen]);

  // Get sorted data
  const sortedMrnData = getSortedData(filteredMrnData);
  
  // Pagination logic
  // const totalPages = Math.ceil(sortedMrnData.length / itemsPerPage);
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const endIndex = startIndex + itemsPerPage;
  // const currentItems = sortedMrnData.slice(startIndex, endIndex);
  // Use this instead:
const currentItems = mrnData;
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Render pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${
          currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'
        } rounded-none`}
      >
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${
            i === currentPage ? 'bg-blue-600 text-white rounded-none' : 'text-blue-600 hover:bg-blue-50 rounded-none'
          }`}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${
          currentPage === totalPages || totalPages === 0
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-blue-600 hover:bg-blue-50'
        } rounded-none`}
      >
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');

  // 🔒 MRN LOCK RULES

  // const isEditLocked = (item) => {
  //   // 1. If Rejected, always locked
  //   if (item.ApprovalStatus === 'REJECTED') return true;

  //   // 2. If Current Level > 1, locked
  //   if (item.CurrentLevel > 1) return true;

  //   // 3. If Level 1 is Approved, locked
  //   if (item.CurrentLevel === 1 && item.ApprovalStatus === 'APPROVED') return true;
  //   // Only lock if SendToBC or Validate is true
  //   if (item.ApprovalStatus === 'APPROVED' && item.DocumentApprovalID) {
  //     return true;
  //   }

  //   // 4. Specific Logic: "Approved but no DocumentApprovalID"
  //   // Only lock if SendToBC or Validate is true
  //   if (item.ApprovalStatus === 'APPROVED' && !item.DocumentApprovalID) {
  //     return item.SendToBC === true || item.ValidateData === true;
  //   }

  //   // 5. Standard Lock: If Sent to BC or Validated
  //   if (item.SendToBC === true || item.ValidateData === true) return true;

  //   return false;
  // };


    const isEditLocked = (item) => {
    // 1. If Sent to BC, always Locked
    if (item.SendToBC === true || item.ValidateData === true) return true;

    // 2. If Rejected, always Locked
    if (item.ApprovalStatus === 'REJECTED') return true;

    if (!item.ApprovalFlowID && !item.ValidateData) {
      return false;
    }

    // 3. If Fully Approved (All levels done), Locked for EVERYONE
    // This ensures neither entry user nor approvers can edit after final approval.
    if (item.ApprovalStatus === 'APPROVED') return true;

    // 4. If Pending Approval
    if (item.ApprovalStatus === 'PENDING' && item.CurrentLevel > 1) {
      // 'CanCurrentUserApprove' is calculated in SQL.
      // It is TRUE only if the logged-in user/role matches the CurrentApprover of the active level.
      // It effectively restricts edit rights to ONLY the current level's approver.
      if (item.CanCurrentUserApprove) {
        return false; // Unlocked for this specific approver
      }
      return true; // Locked for Entry User, Previous Approvers, and everyone else
    }

    // 5. Default (Draft/No Flow) - Unlocked
    return false;
  };

  // const isDeleteLocked = (item) => {
  //   // "Same case with delete"
    
  //   // 1. If Rejected, always locked
  //   // if (item.ApprovalStatus === 'REJECTED') return true;

  //   // 2. If Current Level > 1, locked
  //   if (item.CurrentLevel > 1) return true;

  //   if (item.ApprovalStatus === 'APPROVED' && item.DocumentApprovalID) {
  //     return true;
  //   }

  //   // 3. If Level 1 is Approved, locked
  //   if (item.CurrentLevel === 1 && item.ApprovalStatus === 'APPROVED') return true;

  //   // 4. Specific Logic: "Approved but no DocumentApprovalID"
  //   if (item.ApprovalStatus === 'APPROVED' && !item.DocumentApprovalID) {
  //     return item.SendToBC === true || item.ValidateData === true;
  //   }

  //   // 5. Standard Lock: If Sent to BC or Validated
  //   if (item.SendToBC === true || item.ValidateData === true) return true;

  //   return false;
  // };

  const isDeleteLocked = (item) => {
    const level = item.CurrentLevel ?? 0;
    // "Same case with delete"
    
    // 1. If Rejected, always locked
    // if (item.ApprovalStatus === 'REJECTED') return true;

    // 2. If Current Level > 1, locked
    if (item.CurrentLevel > 1) return true;

    if (item.ApprovalStatus === 'APPROVED' && item.DocumentApprovalID) {
      return true;
    }
     if (item.ApprovalStatus === 'APPROVED' && !item.DocumentApprovalID && item.ValidateData === false) {
      return false ;
    }


    // 3. If Level 1 is Approved, locked
    if (item.CurrentLevel === 1 && item.ApprovalStatus === 'APPROVED') return true;

    // 4. Specific Logic: "Approved but no DocumentApprovalID"
    if (item.ApprovalStatus === 'APPROVED' && !item.DocumentApprovalID) {
      return item.SendToBC === true || item.ValidateData === true;
    }

    // 5. Standard Lock: If Sent to BC or Validated
    if (item.SendToBC === true || item.ValidateData === true) return true;

    return false;
  };
  // console.log('Permissions:', { canCreate, canView, canEdit, canDelete, hasFullAccess });
  
  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: COLORS.ANGEL }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className="min-h-screen flex"
      style={{
        backgroundColor: COLORS.ANGEL,
        '--accent-color': COLORS.ACCENT,
        '--accent-hover': COLORS.ACCENT_HOVER,
        '--page-border-color': COLORS.CARD_BORDER,
        '--sidebar-bg': '#ffffff',
        '--page-background': COLORS.ANGEL
      }}
    >
      <Sidebar collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ activeBg: '#417fe2ff', activeText: '#ffffffff', profileBg: '#417fe2ff' }}
        onLogout={handleLogout}/>
      <main className={`flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Material Receipt Note
              </h1>
              <p className="text-gray-600">
                Manage your material receipts and incoming goods here.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={handleDownloadCSV}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 font-medium transition-all duration-200 shadow-sm rounded-none w-full sm:w-auto"
              >
                <FaDownload className="h-4 w-4" />
                <span>Export</span>
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm rounded-none w-full sm:w-auto"
                >
                  <FaPlus className="h-4 w-4" />
                  <span>Create New</span>
                </button>
              )}
              {hasFullAccess && (
                <button
                  onClick={() => setIsPDFDesignerOpen(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm rounded-none w-full sm:w-auto"
                >
                  <FaEdit className="h-4 w-4" />
                  <span>Design PDF</span>
                </button>
              )}
            </div>
          </div>
          <div style={{ height: 1, backgroundColor: COLORS.DIVIDER, marginTop: 16, marginBottom: 20 }} />
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
          {[
            { label: 'Total Pending Approval', value: summaryData.pendingApproval, icon: 'clock', color: 'amber' },
            { label: 'Total Pending Validate', value: summaryData.pendingValidate, icon: 'check', color: 'blue' },
            { label: 'Total Pending Send to BC', value: summaryData.pendingSendToBC, icon: 'upload', color: 'green' },
            { label: 'This Month', value: summaryData.thisMonth, icon: 'calendar', color: 'purple' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-4 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-800">
                  {stat.icon === 'document' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V4a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {stat.icon === 'check' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {stat.icon === 'clock' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {stat.icon === 'calendar' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Search & Filter */}
        <section className="p-4 bg-white border border-slate-200 shadow-sm mb-6 rounded-none">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search MRN number, vendor, etc."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none"
              />
            </div>
                     {/* --- UPDATED: BC Status Filter UI --- */}
          <div className="">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Process Status</label>
            <div className="flex flex-wrap gap-4 text-sm w-full py-2 px-3 bg-white border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none">
              {['Pending', 'Approved', 'Rejected', 'Validate', 'Send to BC', 'Data Successfully Sent'].map((label) => (
                <label key={label} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    checked={selectedStatusFilters.includes(label)}
                    onChange={() => handleStatusFilterChange(label)}
                  />
                  <span className="text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
          {/* <div className="w-full sm:w-48">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full py-2 px-3 bg-white border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none"
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div> */}
          {/* ------------------------------------ */}
            {/* <div className="self-end">
              <button 
                className="w-full sm:w-auto px-5 py-2 bg-blue-500 text-white font-medium hover:bg-blue-400 transition-all duration-200 shadow rounded-none"
                onClick={fetchMRNData}
              >
                Search
              </button>
            </div> */}
          </div>

          {/* --- ADDED: BC Status Filter UI --- */}
          
          {/* -------------------------------- */}
        </section>
        
        {/* Table */}
        <section className="shadow-sm overflow-hidden rounded-none border border-slate-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4" style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${COLORS.DIVIDER}` }}>
            <h2 className="text-xl font-semibold text-slate-900 mb-2 sm:mb-0">
              Material Receipt Notes
            </h2>
            <div className="flex items-center gap-2">{renderPaginationButtons()}</div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-slate-600">
              <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
              <p>Loading material receipt notes...</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto" style={{ backgroundColor: '#ffffff' }}>
                <table className="w-full text-sm">
                  <thead className="bg-[#D5F0F5] text-slate-700 text-xs text-left font-semibold uppercase">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('MRNNumber')}
                      >
                        <div className="flex items-center uppercase">
                          MRN No.
                          {renderSortArrows('MRNNumber')}
                        </div>
                      </th>
                      {/* <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('MRNDate')}
                      >
                        <div className="flex items-center">
                          MRN Date
                          {renderSortArrows('MRNDate')}
                        </div>
                      </th> */}
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('vendor_name')}
                      >
                        <div className="flex items-center">
                          Supplier Name
                          {renderSortArrows('vendor_name')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('ReceivedBy')}
                      >
                        <div className="flex items-center">
                          Received By
                          {renderSortArrows('ReceivedBy')}
                        </div>
                      </th>
                      {/* <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('TotalItems')}
                      >
                        <div className="flex items-center">
                          Total Items
                          {renderSortArrows('TotalItems')}
                        </div>
                      </th> */}
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('BillDate')}
                      >
                        <div className="flex items-center">
                          Bill Date
                          {renderSortArrows('BillDate')}
                        </div>
                      </th>
                      <th 
  className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
  onClick={() => handleSort('ProjectCode')}
>
  <div className="flex items-center uppercase">
    Project
    {renderSortArrows('ProjectCode')}
  </div>
</th>
<th 
  className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
  onClick={() => handleSort('VendorInvoiceNo')}
>
  <div className="flex items-center uppercase">
    Vendor Invoice No.
    {renderSortArrows('VendorInvoiceNo')}
  </div>
</th>
                      {/* <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('Status')}
                      >
                        <div className="flex items-center">
                          Status
                          {renderSortArrows('Status')}
                        </div>
                      </th> */}
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                        onClick={() => handleSort('Remarks')}
                      >
                        <div className="flex items-center">
                          Remarks
                          {renderSortArrows('Remarks')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200" 
                       
                      >
                        <div className="flex items-center">
                          Approval/BC Status
                          
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: COLORS.DIVIDER }}>
                    {currentItems.length > 0 ? (
                      currentItems.map((item) => {
                        const isActive = item.Status === 1 || item.Status === true;
                        const statusText = isActive ? 'Active' : 'Inactive';
                        
                        return (
                          <tr key={item.MRNNumber} className="hover:bg-slate-50 transition-colors duration-200">
                            <td className="px-6 py-3 text-left font-medium text-slate-900">{item.MRNNumber}</td>
                            {/* <td className="px-6 py-3 text-left text-slate-700 uppercase">{item.MRNDate ? new Date(item.MRNDate).toLocaleDateString() : ''}</td> */}
                            <td className="px-6 py-3 text-left text-slate-700 uppercase">{item.vendor_name || ''}</td>
                            <td className="px-6 py-3 text-left text-slate-700 uppercase">{item.ReceivedBy || ''}</td>
                            {/* <td className="px-6 py-3 text-left text-slate-700">{item.TotalItems || 0}</td> */}
                            <td className="px-6 py-3 text-left text-slate-700">{formatDate(item.BillDate)}</td>
                            <td className="px-6 py-3 text-left text-slate-700 uppercase">{item.ProjectCode || 'N/A'}</td>
                            <td className="px-6 py-3 text-left text-slate-700 uppercase">{item.VendorInvoiceNo || 'N/A'}</td>
                            {/* <td className="px-6 py-3">
                              <span 
                                className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer transition-colors duration-200 ${
                                  isActive 
                                    ? 'bg-teal-50 text-teal-600 hover:bg-teal-200' 
                                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                } rounded-full`}
                                onClick={() => canEdit && toggleMRNStatus(item.MRNNumber, isActive)}
                                title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                              >
                                {statusText}
                              </span>
                            </td> */}
                              <td className="px-6 py-3 max-w-xs truncate text-slate-700 uppercase" title={item.Remarks || ''}>
                              {item.Remarks || 'N/A'}
                            </td>
                            <td className='px-6 py-3'>
  {/*
    LOGIC:
    1. PENDING    -> Approval flow
    2. REJECTED   -> Show Rejected only
    3. COMPLETED / NULL -> Validate / Send to BC flow
  */}

  {item.ApprovalStatus === 'PENDING' ? (
    // --- APPROVAL PHASE ---
    item.CanCurrentUserApprove ? (
      <button 
        onClick={() => openApprovalModal(item.MRNDocumentId, item.MRNNumber,  item.CurrentLevel)}
        className="flex items-center gap-1 text-blue-600 border border-blue-200 px-2 py-1 rounded hover:bg-blue-50 text-xs font-semibold"
      >
        Approve (Lvl {item.CurrentLevel})
      </button>
    ) : (
      <span className="text-xs text-gray-500 italic flex items-center gap-1">
        Approval is going on (Lvl {item.CurrentLevel})
      </span>
    )

  ) : item.ApprovalStatus === 'REJECTED' ? (
    // --- REJECTED STATE ---
    <span className="text-xs font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-200">
      Rejected
    </span>

  ) : (
    // --- POST-APPROVAL PHASE ---
    <>
      {!item.ValidateData && (
        <button 
          onClick={() => handleValidateData(item.MRNDocumentId,item.MRNNumber)}
          className="text-blue-600 hover:text-blue-800 transition-colors duration-200 rounded-none p-1 flex items-center gap-1 text-xs font-semibold border border-blue-200 px-2"
          title="Validate Data"
        >
          Validate Data
        </button>
      )}

      {item.ValidateData && !item.SendToBC && (
        <button 
          onClick={() => handleSendToBC(item.MRNDocumentId,item.MRNNumber)}
          className="text-green-600 hover:text-green-800 transition-colors duration-200 rounded-none p-1 flex items-center gap-1 text-xs font-semibold border border-green-200 px-2"
          title="Send to BC"
        >
          Send to BC
        </button>
      )}

      {item.ValidateData && item.SendToBC && (
        <span className="text-xs font-semibold text-green-600 flex items-center gap-1 bg-green-50 px-2 py-1 rounded border border-green-200">
          Data Successfully Sent
        </span>
      )}
    </>
  )}
</td>


                          
                            <td className="px-6 py-3">
                              <div className="flex gap-3 justify-center">
                                
                                
                                {canView && (
                                  <button 
                                    onClick={() => handleView(item)}
                                    className="text-slate-800 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                                    title="View"
                                  >
                                    <FaEye className="h-4 w-4" />
                                  </button>
                                )}
                                {canEdit && (
  <button
    onClick={() => !isEditLocked(item) && handleEdit(item)}
    disabled={isEditLocked(item)}
    title={
      isEditLocked(item)
        ? 'Edit disabled after approval / rejection'
        : 'Edit'
    }
    className={`p-1 ${
      isEditLocked(item)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-blue-600'
    }`}
  >
    <FaEdit />
  </button>
)}

                                {canDelete && (
  <button
    onClick={() => !isDeleteLocked(item) && handleDelete(item.MRNDocumentId)}
    disabled={isDeleteLocked(item)}
    title={
      isDeleteLocked(item)
        ? 'Delete disabled after approval / rejection / BC'
        : 'Delete'
    }
    className={`p-1 ${
      isDeleteLocked(item)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-red-600'
    }`}
  >
    <FaTrash />
  </button>
)}

                                <button 
                                  onClick={() => downloadPDF(item.MRNNumber)}
                                  className="text-slate-700 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                                  title="Download PDF"
                                >
                                  <FaDownload className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center py-8 text-slate-600">
                          No material receipt notes found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile Card View */}
              <div className="md:hidden p-4 space-y-4" style={{ backgroundColor: '#ffffff' }}>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => {
                    const isActive = item.Status === 1 || item.Status === true;
                    const statusText = isActive ? 'Active' : 'Inactive';
                    
                    return (
                      <div key={item.MRNNumber} className="border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-slate-900">{item.MRNNumber}</h3>
                          <span 
                            className={`inline-block px-2 py-1 text-xs font-medium cursor-pointer transition-colors duration-200 ${
                              isActive 
                                ? 'bg-teal-50 text-teal-600 hover:bg-teal-200' 
                                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            } rounded-full`}
                            onClick={() => canEdit && toggleMRNStatus(item.MRNNumber, isActive)}
                            title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                          >
                            {statusText}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Date</p>
                            <p className="text-slate-700">{item.MRNDate ? new Date(item.MRNDate).toLocaleDateString() : ''}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Supplier</p>
                            <p className="text-slate-700 uppercase">{item.vendor_name || ''}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Received By</p>
                            <p className="text-slate-700 uppercase">{item.ReceivedBy || ''}</p>
                          </div>
                          {/* <div>
                            <p className="text-xs text-slate-500">Items</p>
                            <p className="text-slate-700">{item.TotalItems || 0}</p>
                          </div> */}
                          <div>
                            <p className="text-xs text-slate-500">Bill Date</p>
                            <p className="text-slate-700">{item.BillDate ? new Date(item.BillDate).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-slate-500">Project</p>
                              <p className="text-slate-700 uppercase">{item.ProjectCode || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Vendor Invoice</p>
                              <p className="text-slate-700 uppercase">{item.VendorInvoiceNo || 'N/A'}</p>
                            </div>
                        </div>
                        
                        {item.Remarks && (
                          <div className="mt-2">
                            <p className="text-xs text-slate-500">Remarks</p>
                            <p className="text-slate-700 text-sm truncate" title={item.Remarks}>{item.Remarks}</p>
                          </div>
                        )}
                        
                        <div className="flex justify-end gap-2 mt-3">
                          {canView && (
                            <button 
                              onClick={() => handleView(item)}
                              className="text-slate-800 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                              title="View"
                            >
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            <button 
                              onClick={() => handleEdit(item)}
                              className="text-teal-700 hover:text-teal-900 transition-colors duration-200 rounded-none p-1"
                              title="Edit"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button 
                              onClick={() => handleDelete(item.MRNNumber)}
                              className="text-rose-600 hover:text-rose-800 transition-colors duration-200 rounded-none p-1"
                              title="Delete"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                          <button 
                            onClick={() => downloadPDF(item.MRNNumber)}
                            className="text-slate-700 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                            title="Download PDF"
                          >
                            <FaDownload className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-slate-600">
                    No material receipt notes found
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </main>
      
      {/* Create/Edit Modal */}
      {isModalOpen && (
        <MaterialReceiptNoteFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          mode={editData ? 'edit' : 'create'}
          initialData={editData}
          onSubmit={handleFormSubmit}
          onFormSuccess={handleFormSuccess}
        />
      )}
      
      {/* View Modal */}
      {isViewModalOpen && (
        <MaterialReceiptViewModal
          open={isViewModalOpen}
          onClose={handleCloseViewModal}
          mrnId={viewData}
        />
      )}

      
      
      {/* PDF Designer Modal */}
      {isPDFDesignerOpen && (
  <PDFDesigner
    isOpen={isPDFDesignerOpen}
    onClose={() => setIsPDFDesignerOpen(false)}
    entity="Material Receipt Note"

    sampleData={{
      MRNNumber: "MRN0001",
      VendorCode:"V00001",
      VendorName: "ABC Supplier Pvt Ltd",
      LocationCode: "LOC001",
      BranchCode: "BR001",
      BranchName: "Mumbai",
      ReceivedBy: "Rahul",
      VendorInvoiceNo: "INV-001",
      InvoiceReceivedDate: "2025-03-01",
      DeliveryNoteNumber: "DN-01",
      ChallanNumber: "CH-01",
      EWayBillNumber:"EW09097",
      VehicleNumber:"DL00001",
      Transporter:"YASH",
      GRNumber:"G090909",
      ProjectCode:"PR00001",
      // PendingBillNumber: "PB-01",
      BillDate: "2025-03-05",
      Remarks: "Material received successfully",
      Items: [
        {
          ProductCode: "ITEM001",
          ProductName: "Transformer",
          UOM: "PCS",
          ReceivedQty: 10,
          ShortageQty: 0,
          WarehouseCode: "WH001",
          BranchCode: "BR001",
          PurchaseOrderNumber: "PO0001",
          POLineNumber: 1,
          BCReceiptNo: "BC1001",
          ProductTrackingType: 1,
          Remarks: "Good condition"
        }
      ],
      Summary: {
        totalItems: 1,
        totalReceivedQuantity: 10,
       
      }
    }}

    availableFields={{
      Header: [
        "MRNNumber",
        "VendorCode",
        "VendorName",
        "LocationCode",
        "BranchCode",
        "BranchName",
        "ReceivedBy",
        "VendorInvoiceNo",
        "InvoiceReceivedDate",
        "DeliveryNoteNumber",
        "ChallanNumber",
        "EWayBillNumber",
        "VehicleNumber",
        "Transporter",
        "GRNumber",
        "ProjectCode",
        // "PendingBillNumber",
        "BillDate",
        "Remarks",
       
        "ApprovalStatus"
      ],
      Items: [
        "ProductCode",
        "ProductName",
        "UOM",
        "ReceivedQty",
        "ShortageQty",
        "WarehouseCode",
        "BranchCode",
        "PurchaseOrderNumber",
        "POLineNumber",
        "BCReceiptNo",
        "ProductTrackingType",
        "Remarks"
      ],
      Summary: [
        "totalItems",
        "totalReceivedQuantity"
      
      ]
    }}
  />
)}

       
      <ConfirmationDialog 
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        isDanger={confirmModal.isDanger}
      />

       <ActionModal 
        isOpen={actionModal.isOpen}
        status={actionModal.status}
        message={actionModal.message}
        onClose={() => setActionModal({ ...actionModal, isOpen: false })}
      />

      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`
            relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
            ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
            ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
            ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
          `}>
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
          <style>
            {`
             
                /* Add this to the end of your existing style.innerHTML string */
                @keyframes toastIn {
                  from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
                  to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes toastProgress {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .animate-toastIn { animation: toastIn 0.25s ease-out; }
                .animate-toastProgress { animation: toastProgress 4s linear forwards; }

            `}
          </style>
        </div>
      )}

        {/* Approval/Reject Modal */}
  {approvalModal.isOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50">
      <div className="relative bg-white shadow-xl max-w-md w-full mx-4 p-6 rounded-lg animate-in fade-in zoom-in duration-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Review MRN: {approvalModal.mrnNumber} (Level {approvalModal.currentLevel})
        </h3>
        
        {/* Remarks Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Remarks {<span className="text-red-500">*</span>}
          </label>
          <textarea
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Enter remarks..."
            value={approvalModal.remarks}
            onChange={(e) => setApprovalModal({ ...approvalModal, remarks: e.target.value })}
          ></textarea>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between gap-3">
          <button
            onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => handleApprovalAction('REJECTED')}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Reject
            </button>
            <button
              onClick={() => handleApprovalAction('APPROVED')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  )}

        {/* --- SUCCESS MODAL --- */}
      {successModal.isOpen && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-slideDown">
          <div className="flex items-center gap-4 bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 min-w-[420px]">
            {/* Icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <SuccessIcon size={22} />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800">
                Material Receipt Note {successModal.action} Successfully
              </h4>
              <p className="text-xs text-gray-500">
                MRN No: <span className="font-medium">{successModal.mrnNo}</span>
              </p>
            </div>

            {/* Loader bar */}
            <div className="absolute bottom-0 left-0 h-[3px] bg-green-500 rounded-b-xl animate-progress" />
          </div>

          <style>
            {`
              @keyframes slideDown {
                from { opacity: 0; transform: translate(-50%, -10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
              @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
              }
              .animate-slideDown { animation: slideDown 0.25s ease-out; }
              .animate-progress { animation: progress 2s linear; }
              
            `}
          </style>
        </div>
      )}

    </div>
  );
};

export default MaterialReceiptNote;